import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, User, AlertCircle, Smile, Loader, Calendar, Clock, Users, MapPin } from 'lucide-react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { supabase } from '../lib/supabase';
import { getMessages, sendMessage, subscribeToMessages } from '../lib/chat';
import type { Message } from '../lib/chat';
import LoadingIndicator from '../components/LoadingIndicator';

const Chat = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        if (!user) {
          navigate('/login');
          return;
        }
        setCurrentUser(user);

        // Get chat room details to find the other participant
        const { data: room, error: roomError } = await supabase
          .from('chat_rooms')
          .select(`
            *,
            participant1:profiles!chat_rooms_participant1_id_fkey(
              id,
              full_name,
              profile_picture,
              location
            ),
            participant2:profiles!chat_rooms_participant2_id_fkey(
              id,
              full_name,
              profile_picture,
              location
            )
          `)
          .eq('id', roomId)
          .single();

        if (roomError) throw roomError;

        // Set the other user based on the current user's ID
        const otherParticipant = room.participant1_id === user.id ? room.participant2 : room.participant1;
        setOtherUser(otherParticipant);

        // Get messages
        const messagesData = await getMessages(roomId!);
        setMessages(messagesData || []);

        // Subscribe to new messages
        const subscription = subscribeToMessages(roomId!, (message) => {
          setMessages(prev => [...prev, message]);
          scrollToBottom();
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Error initializing chat:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chat');
      } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      initialize();
    }
  }, [roomId, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !roomId) return;

    try {
      setSending(true);
      await sendMessage(roomId, newMessage.trim());
      setNewMessage('');
      setShowEmojiPicker(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage(prev => prev + emojiData.emoji);
  };

  // Function to navigate to user profile when clicking on profile picture
  const navigateToUserProfile = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  // Function to render reservation details in a message
  const renderReservationDetails = (content: string) => {
    if (content.includes('🍽️ I\'ve made a reservation')) {
      // This is a reservation message
      return (
        <div className="bg-blue-50 rounded-lg p-3 mt-2">
          {content.split('\n').map((line, index) => {
            if (line.includes('Date:')) {
              return (
                <div key={index} className="flex items-center text-blue-700">
                  <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>{line.replace('📅 Date:', '').trim()}</span>
                </div>
              );
            } else if (line.includes('Time:')) {
              return (
                <div key={index} className="flex items-center text-blue-700">
                  <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>{line.replace('🕒 Time:', '').trim()}</span>
                </div>
              );
            } else if (line.includes('Party size:')) {
              return (
                <div key={index} className="flex items-center text-blue-700">
                  <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>{line.replace('👥 Party size:', '').trim()}</span>
                </div>
              );
            } else if (line.includes('Special requests:')) {
              return (
                <div key={index} className="flex items-center text-blue-700">
                  <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>{line.replace('📝 Special requests:', '').trim()}</span>
                </div>
              );
            } else {
              return <div key={index}>{line}</div>;
            }
          })}
        </div>
      );
    }
    
    // Regular message
    return <p className="break-words whitespace-pre-wrap">{content}</p>;
  };

  if (loading) {
    return <LoadingIndicator message="Loading chat..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-t-lg shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition flex items-center justify-center"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            {otherUser?.profile_picture ? (
              <img
                src={otherUser.profile_picture}
                alt={otherUser.full_name}
                className="w-12 h-12 rounded-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => navigateToUserProfile(otherUser.id)}
              />
            ) : (
              <div 
                className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors"
                onClick={() => navigateToUserProfile(otherUser.id)}
              >
                <User className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div>
              <h2 
                className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-orange-500 transition-colors"
                onClick={() => navigateToUserProfile(otherUser.id)}
              >
                {otherUser?.full_name}
              </h2>
              {otherUser?.location && (
                <p className="text-sm text-gray-500">{otherUser.location}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white shadow-sm p-4 min-h-[60vh] max-h-[60vh] overflow-y-auto">
        <div className="space-y-4">
          {messages.map((message) => {
            const isOwnMessage = message.sender_id === currentUser?.id;
            
            return (
              <div
                key={message.id}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-end space-x-2 max-w-[70%] ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {!isOwnMessage && (
                    message.sender?.profile_picture ? (
                      <img
                        src={message.sender.profile_picture}
                        alt={message.sender?.full_name}
                        className="w-8 h-8 rounded-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => navigateToUserProfile(message.sender.id)}
                      />
                    ) : (
                      <div 
                        className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors"
                        onClick={() => navigateToUserProfile(message.sender.id)}
                      >
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                    )
                  )}
                  
                  <div
                    className={`rounded-lg px-4 py-2 ${
                      isOwnMessage
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {renderReservationDetails(message.content)}
                    <span className="text-xs opacity-75 mt-1 block">
                      {new Date(message.created_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white rounded-b-lg shadow-sm p-4">
        <form onSubmit={handleSend} className="flex space-x-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Schreibe eine Nachricht..."
              className="w-full p-2 pr-10 border rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            >
              <Smile className="h-5 w-5" />
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-full right-0 mb-2">
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  autoFocusSearch={false}
                  width={300}
                  height={400}
                />
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className={`px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition flex items-center space-x-2 ${
              sending || !newMessage.trim() ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {sending ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>Sendet...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Senden</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;